import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, ResponsiveContainer, Cell } from 'recharts';

import { ReactComponent as DeleteIcon } from '../../../assets/icons/delete-icon.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit.svg';
import { ReactComponent as AddIcon } from '../../../assets/icons/more-icon.svg';
import { ReactComponent as SearchIcon } from '../../../assets/icons/search-icon.svg';
import { ReactComponent as VlbcCloudIcon } from '../../../assets/icons/vlbc-cloud.svg';
import NoDataFound from '../../../assets/images/no-data-found.png';
import DefaultApiLimit from '../../../config/defaultLimit';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { fetchVlbcs, setVlbcSearchBy } from '../../../redux/slice/VlbcSlice';
import BatteryLoader from '../../../shared-components/battery-loader/BatteryLoader';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomInputSwitch from '../../../shared-components/custom-input-switch/CustomInputSwitch';
import DeleteWarning from '../../../shared-components/delete-warning/DeleteWarning';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import Pagination from '../../../shared-components/pagination/Pagination';

import SidePanel from './SidePanel';

interface ListVlbcProperties {
  handleEdit: (id: string, event: React.MouseEvent | React.KeyboardEvent) => void;
  handleUpdateStatus: (id: string, status: boolean) => void;
  handleDelete: (id: string) => void;
}

interface ModalBoxDetail {
  title: string;
  content: JSX.Element;
  notificationModal: boolean;
  rightCorner: string;
}

/**
 * List vlbc component in card view
 */
function ListVlbc(properties: ListVlbcProperties) {
  const { handleUpdateStatus, handleEdit, handleDelete } = properties;
  const { t } = useTranslation('vlbc');
  const dispatch = useAppDispatch();
  const { vlbcs } = useAppSelector((states) => states.vlbc);
  const [showSidePanel, setShowSidePanel] = useState<boolean>(false);
  const [vlbcId, setVlbcId] = useState<string>('');
  const [vlbcDetails, setVlbcDetails] = useState<{
    loadBalancerName: string;
    algorithm: string;
  }>({
    loadBalancerName: '',
    algorithm: '',
  });
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [, setOpen] = useState(false);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: <div />,
    rightCorner: '',
    notificationModal: false,
  });
  const [valueToShow, setValueToShow] = useState('');
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  /**
   * Toggles the dropdown menu's open state.
   */
  const handleToggle = (id: string) => {
    setOpenDropdownId((previous) => (previous === id ? null : id));
  };

  /**
   * Closes the dropdown menu.
   */
  const handleClose = () => {
    setOpen(false);
  };

  /**
   * Handles the delete device warning by opening a modal box with a confirmation message.
   *
   * @param {React.MouseEvent | React.KeyboardEvent} event - The event triggered by the delete action.
   */
  const handleDeleteDeviceWarning = (event: React.MouseEvent | React.KeyboardEvent, id: string) => {
    event.stopPropagation();

    if ('key' in event && event.key !== 'Enter') {
      return;
    }

    setModalBoxStatus('open');

    setModalBoxDetail({
      title: '',
      rightCorner: '',
      content: (
        <DeleteWarning
          title={t('deleteWarningTitle')}
          description={t('deleteWarningDescription')}
          cancelButtonOnClick={() => setModalBoxStatus('close')}
          submitSvgIcon={<DeleteIcon />}
          submitButtonOnClick={() => {
            handleDelete(id);
            setModalBoxStatus('close');
          }}
        />
      ),
      notificationModal: true,
    });
  };

  /**
   * Handle the change page function
   * @param pageNumber
   */
  const handlePageChange = (pageNumber: number) => {
    dispatch(
      fetchVlbcs({
        page: pageNumber,
        limit: vlbcs.limit,
        ...vlbcs.filter,
      }),
    );
  };

  /**
   * Handle the change limit function
   * @param limitCount
   */
  const handleLimitChange = (limitCount: number) => {
    dispatch(
      fetchVlbcs({
        page: DefaultApiLimit.page,
        limit: limitCount,
        ...vlbcs.filter,
      }),
    );
  };

  // Handle input change
  /**
   * Handles changes to the filter input and updates the search value.
   * @param name - The name of the filter.
   * @param value - The value to filter by.
   */
  const handleChangeFilters = (name: string, value: string) => {
    setValueToShow(value);
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      dispatch(fetchVlbcs({ searchBy: value }));
    }, 1000);
    dispatch(setVlbcSearchBy(value));
  };

  // // Debounce fetchVlbcs when valueToShow changes
  // useEffect(() => {
  //   if (debounceTimer.current) {
  //     clearTimeout(debounceTimer.current);
  //   }
  //   debounceTimer.current = setTimeout(() => {
  //     dispatch(fetchVlbcs({ searchBy: valueToShow }));
  //   }, 1000);

  //   return () => {
  //     if (debounceTimer.current) {
  //       clearTimeout(debounceTimer.current);
  //     }
  //   };
  // }, [valueToShow, dispatch]);

  return (
    <div className="list-vlbc-wrap">
      <div className="search-input">
        <CustomInput
          inputLabel=""
          type="text"
          placeholder={t('searchHere')}
          name="searchBy"
          svgIcon={<SearchIcon />}
          value={valueToShow}
          onChangeValue={(_, value) => handleChangeFilters('searchBy', String(value))}
        />
      </div>

      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        notification={modalBoxDetail.notificationModal}
        header={!modalBoxDetail.notificationModal}
      />
      {showSidePanel && (
        <SidePanel
          closeModal={() => {
            setShowSidePanel(!showSidePanel);
            setVlbcId('');
          }}
          vlbcId={vlbcId}
          vlbcDetails={vlbcDetails}
        />
      )}

      {(() => {
        let cardview = null;

        if (vlbcs.loading) {
          cardview = (
            <div className="table-loading">
              <BatteryLoader />
            </div>
          );
        } else if (vlbcs.data?.length > 0 && !vlbcs.loading) {
          cardview = (
            <div className="card-wrap">
              {vlbcs.data.map((item) => (
                <div
                  className="card"
                  key={`vlbc-card-${item}`}
                  onClick={() => {
                    setVlbcId(item._id);
                    setShowSidePanel(!showSidePanel);
                    setVlbcDetails({
                      loadBalancerName: item?.loadBalancerName || '',
                      algorithm: item?.algorithm || '',
                    });
                  }}
                  role="presentation">
                  <div className="card-header">
                    <div className="card-header-left">
                      <p>{t('DeActive')}</p>
                      <CustomInputSwitch
                        inputLabel=""
                        value={item.isActive}
                        name="isActive"
                        onChangeValue={() => {
                          handleUpdateStatus(item._id, item.isActive);
                        }}
                      />
                      <p>{t('active')}</p>
                    </div>
                    <div className="card-header-right">
                      <span>
                        <VlbcCloudIcon />
                      </span>
                      <div className="dropdown-wrapper">
                        <div
                          className="dropdown-icon"
                          onClick={(previous) => {
                            previous.stopPropagation();
                            handleToggle(item._id);
                          }}
                          onKeyDown={(previous) => {
                            if (previous.key === 'Enter') {
                              previous.stopPropagation();
                              handleToggle(item._id);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                          aria-label="Toggle dropdown menu">
                          <AddIcon />
                        </div>

                        {openDropdownId === item._id && (
                          <div className="dropdown-menu">
                            <div
                              className="dropdown-item"
                              onClick={handleClose}
                              onKeyDown={(previous) => previous.key === 'Enter' && handleClose()}
                              role="button"
                              tabIndex={0}>
                              <span
                                onClick={(event) => handleEdit(item._id, event)}
                                role="presentation">
                                <EditIcon className="icon edit" />
                                <span>{t('edit')}</span>
                              </span>
                            </div>
                            <div className="borders"> </div>
                            <div
                              className="dropdown-item"
                              onClick={(event) => handleDeleteDeviceWarning(event, item._id)}
                              onKeyDown={(event) => handleDeleteDeviceWarning(event, item._id)}
                              role="button"
                              tabIndex={0}>
                              <span>
                                <DeleteIcon className="icon delete" />
                                <span>{t('delete')}</span>
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="card-body">
                    <div className="wrap">
                      <div className="left">
                        <div className="basic-data">
                          <div className="group">
                            <div className="key">{t('loadBalancerName')}</div>
                            <div className="value">{item.loadBalancerName}</div>
                          </div>
                          <div className="group">
                            <div className="key">{t('loadBalancerID')}</div>
                            <div className="value">{item._id}</div>
                          </div>
                          <div className="group">
                            <div className="key">{t('maxPower')}</div>
                            <div className="value">{`${item.maximumOutputPower} Amps`}</div>
                          </div>
                          <div className="group">
                            <div className="key">{t('loadBalancerType')}</div>
                            <div className="value">{item.loadBalancerType}</div>
                          </div>
                          <div className="group">
                            <div className="key">{t('algorithm')}</div>
                            <div className="value">{item.algorithm}</div>
                          </div>
                        </div>
                        <hr />
                        <div className="usage">
                          <div className="group">
                            <div className="key">{t('pointsConnected')}</div>
                            <div className="value">{item.totalChargingPoints}</div>
                          </div>
                          <div className="group">
                            <div className="key">{t('online')}</div>
                            <div className="value">{item.onlineChargingPoints}</div>
                          </div>
                          <div className="group">
                            <div className="key">{t('offline')}</div>
                            <div className="value">{item.offlineChargingPoints}</div>
                          </div>
                        </div>
                      </div>
                      <div className="right">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={item.processedData}>
                            <XAxis
                              dataKey="labelText"
                              style={{
                                fontWeight: 600,
                                fontSize: '14px',
                              }}
                              axisLine={false}
                              tickLine={false}
                              tick={{ fill: '#666', fontSize: 12 }}
                            />
                            <defs>
                              {item.processedData.map((entry, index) => {
                                const percentage = (entry.value / 100) * 100;

                                const stops = [];

                                if (percentage < 33.33) {
                                  stops.push(
                                    <stop key="stop1" offset="100%" stopColor="#4ABDFB" />,
                                  );
                                }

                                if (percentage < 66.66) {
                                  stops.push(
                                    <>
                                      <stop key="stop3" offset="0%" stopColor="#2AA700" />
                                      <stop key="stop2" offset="50.66%" stopColor="#4ABDFB" />
                                      <stop key="stop1" offset="100%" stopColor="#4ABDFB" />
                                    </>,
                                  );
                                }

                                if (percentage > 66.66) {
                                  stops.push(
                                    <>
                                      <stop key="stop3" offset="0%" stopColor="#E41010" />
                                      <stop key="stop2" offset="66.66%" stopColor="#2AA700" />
                                      <stop key="stop1" offset="100%" stopColor="#4ABDFB" />
                                    </>,
                                  );
                                }

                                return (
                                  <linearGradient
                                    key={`gradient-${entry}`}
                                    id={`gradient-${index}`}
                                    x1="0"
                                    y1="0"
                                    x2="0"
                                    y2="1">
                                    {stops}
                                  </linearGradient>
                                );
                              })}
                            </defs>

                            <Bar dataKey="value" barSize={37} radius={6}>
                              {item.processedData.map((entry, index) => (
                                <Cell
                                  key={`cell-${entry}`}
                                  fill={
                                    entry.label === 'Used' ? `url(#gradient-${index})` : '#4ABDFB'
                                  }
                                />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          );
        } else if (vlbcs.data?.length === 0 && !vlbcs.loading) {
          cardview = (
            <div className="table-no-data-wrap">
              <img src={NoDataFound} alt="" className="no-data-found-image" />
              <div className="no-data-found-details">
                <h3>{t('oops')}</h3>
                <p>{t('oopsDescription')}</p>
              </div>
            </div>
          );
        }

        return cardview;
      })()}

      {vlbcs.data?.length > 0 && !vlbcs.loading && (
        <div className="pagination">
          <Pagination
            activePage={vlbcs.page}
            handlePageChange={handlePageChange}
            size={vlbcs.totalPages}
            totalResults={vlbcs.totalResults}
            limit={vlbcs.limit}
            handleLimitChange={handleLimitChange}
          />
        </div>
      )}
    </div>
  );
}

export default ListVlbc;
